import { messageByShort, versionsFor } from '@/data/iso20022';
import { editorPairFor } from '@/lib/editorPairs';
import { canonicalId, namespaceFor, parseMessageId } from '@/lib/messageId';

/**
 * Pick the response-message revision that best matches a primary version
 * (same country/guideline, else shared market, else an ISO “current” entry).
 */
export function pairVersionId(primaryShort: string, primaryVersionId: string): string | undefined {
  const pairShort = editorPairFor(primaryShort);
  if (!pairShort) return undefined;
  const primaryMsg = messageByShort(primaryShort);
  const pairMsg = messageByShort(pairShort);
  if (!pairMsg) return undefined;

  const versions = versionsFor(pairMsg);
  if (versions.length === 0) return undefined;

  const primaryVersion =
    primaryMsg &&
    versionsFor(primaryMsg).find((v) => canonicalId(v.id) === canonicalId(primaryVersionId));
  const parts = parseMessageId(primaryVersionId);

  if (parts.valid && parts.country && parts.guideline) {
    const national = versions.find((v) => {
      const p = parseMessageId(v.id);
      return p.country === parts.country && p.guideline === parts.guideline;
    });
    if (national) return national.id;
  }

  const markets = primaryVersion?.markets ?? [];
  if (markets.length > 0) {
    const byMarket = versions.find(
      (v) => !parseMessageId(v.id).country && v.markets.some((m) => markets.includes(m)),
    );
    if (byMarket) return byMarket.id;
  }

  const isoCurrent = versions.find((v) => {
    const p = parseMessageId(v.id);
    return !p.country && v.status === 'current';
  });
  if (isoCurrent) return isoCurrent.id;

  const isoAny = versions.find((v) => !parseMessageId(v.id).country);
  return isoAny?.id ?? versions[0]?.id;
}

/** Fields builders need when the user switches schema version / variant / country. */
export function versionInputPatch(
  primaryVersionId: string,
  pairId?: string,
): { xmlns: string; orgnlMsgNmId: string; pairXmlns: string } {
  const pair = pairId ?? primaryVersionId;
  return {
    xmlns: namespaceFor(primaryVersionId),
    orgnlMsgNmId: canonicalId(primaryVersionId),
    pairXmlns: namespaceFor(pair),
  };
}
